import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { PATIENT_BY_IDENTIFIER_QUERY, useMedplum } from '@eluve/medplum-client';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

/**
 * Self-heal for the patient profile: patients without a recent appointment
 * have no FHIR Patient — the appointment-start sync is the only other mint
 * path. When the identifier lookup comes back empty, ask the API to
 * idempotently provision one (ENG-5361), then re-query so every consumer of
 * the shared FHIR Apollo cache picks it up.
 *
 * Fires at most once per mount. For tenant-members the empty list can also
 * mean the AccessPolicy filtered the Patient out — ensure still succeeds,
 * but the refetch stays empty by design.
 */
export const useEnsureFhirPatient = (patientId: string) => {
  const tenantId = useAssignedTenantIdFromParams();
  const apiClient = useApiClient();
  const { fhirClient } = useMedplum();

  const [ensureState, setEnsureState] = useState<'idle' | 'pending' | 'done'>(
    'idle',
  );

  const { data, loading, refetch } = useQuery(PATIENT_BY_IDENTIFIER_QUERY, {
    client: fhirClient ?? undefined,
    variables: { identifier: patientId },
    skip: !fhirClient,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (ensureState !== 'idle') return;
    if (!fhirClient || !tenantId) return;
    if (loading || !data) return;
    if (data.PatientList?.[0]) {
      setEnsureState('done');
      return;
    }

    setEnsureState('pending');
    apiClient.medplum
      .ensurePatient({ params: { tenantId, patientId }, body: {} })
      .then((res) => (res.status === 200 ? refetch() : undefined))
      .catch(() => undefined)
      .finally(() => setEnsureState('done'));
  }, [
    ensureState,
    fhirClient,
    tenantId,
    loading,
    data,
    apiClient,
    patientId,
    refetch,
  ]);
};
